import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CustomProduct, Price, UnitPrice, Units } from '@product/const';
import { mapUnitPrices } from '@product/utils';
import { AuthService, StateUtils } from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CustomProductActions } from '../store/actions';
import { StateWithProduct } from '../store/product-state';
import { ProductSelectors } from '../store/selectors';

type ProductItem = Pick<
  CustomProduct,
  'multiunit' | 'code' | 'unit' | 'unitPrices' | 'units' | 'price' | 'crossedPrice' | 'transitPrice' | 'personalPrice'
>;

@Injectable({ providedIn: 'root' })
export class PriceService {
  private previousAuthState = new Map<string, boolean>();

  constructor(
    private store: Store<StateWithProduct>,
    private authService: AuthService,
  ) {}

  init(product: ProductItem): void {
    combineLatest({ productPrices: this.getPrices(product.code), isUserLoggedIn: this.authService.isUserLoggedIn() })
      .pipe(take(1))
      .subscribe(({ productPrices, isUserLoggedIn }): void => {
        const shouldRefresh = this.shouldRefreshPrices(product.code, isUserLoggedIn);

        if (productPrices && !shouldRefresh) {
          return;
        }

        const { units, price, crossedPrice, transitPrice, personalPrice } = product;
        const firstUnit = units?.[0] || ({ baseUnit: product.unit } as Units);
        const { baseUnit: unit, ...restUnits } = firstUnit;

        const unitPrices: UnitPrice[] = product.unitPrices || [
          { ...restUnits, unit, price, crossedPrice, transitPrice, personalPrice },
        ];
        const prices: Price[] = mapUnitPrices(unitPrices);

        const needLoading = !product.unitPrices && product.multiunit;

        this.setPrice(product.code, prices, needLoading);
      });
  }

  loadPricesIfNeed(productCode: string): void {
    this.getPricesState(productCode)
      .pipe(take(1))
      .subscribe((state) => {
        if (!state || (!state.error && !state.success && !state.loading)) {
          this.store.dispatch(new CustomProductActions.LoadPrices(productCode));
        }
      });
  }

  loadPrices(productCode: string): void {
    this.store.dispatch(new CustomProductActions.LoadPrices(productCode));
  }

  getPricesState(productCode: string): Observable<StateUtils.LoaderState<Price[]>> {
    return this.store.select(ProductSelectors.getPricesState(productCode));
  }

  getPrices(productCode: string): Observable<Price[]> {
    return this.store.select(ProductSelectors.getPricesValue(productCode));
  }

  getPricesByUnit({ unitCode, productCode }: { unitCode: string; productCode: string }): Observable<Price[]> {
    return this.getPrices(productCode).pipe(
      filter<Price[]>(Boolean),
      map((prices) => prices.filter((price) => price.unit?.code === unitCode)),
    );
  }

  private setPrice(productCode: string, prices: Price[], needLoading?: boolean): void {
    if (needLoading) {
      this.store.dispatch(new CustomProductActions.SetPrices(productCode, prices));
    } else {
      this.store.dispatch(new CustomProductActions.LoadPricesSuccess(productCode, prices));
    }
  }

  private shouldRefreshPrices(productCode: string, isUserLoggedIn: boolean): boolean {
    const previousState = this.previousAuthState.get(productCode);
    const authChanged = previousState !== undefined && previousState !== isUserLoggedIn;

    this.previousAuthState.set(productCode, isUserLoggedIn);

    return authChanged;
  }
}
